import EditorialSection from 'components/EditorialSection';
import Section from 'components/Section';
import { forwardRef, useCallback } from 'react';
import './index.css';

/* Capability rows — mono index + Fraunces title + one-line description.
   Sapphire is the working accent; one emerald touch is reserved for RL. */
const CAPABILITIES = [
  {
    id: 'llm-rag',
    label: 'LLMs & RAG',
    description:
      'Building retrieval-augmented generation into full-stack products — grounding language models in real data.',
    accent: 'sapphire',
  },
  {
    id: 'rl',
    label: 'Reinforcement Learning',
    description:
      'Agents that learn from interaction, improving their policy through reward and feedback over time.',
    accent: 'emerald',
  },
  {
    id: 'cv',
    label: 'Computer Vision & AI',
    description:
      'Image processing and applied AI — from classical pipelines to learned models.',
    accent: 'sapphire',
  },
];

const num = i => String(i + 1).padStart(2, '0');

const AI = forwardRef(({ id, sectionRef }, ref) => {
  const titleId = `${id}-title`;

  const askMyAI = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(
      new CustomEvent('askai:open', {
        detail: { question: 'What is your AI experience?' },
      })
    );
  }, []);

  return (
    <Section
      as="section"
      className="ai"
      id={id}
      ref={sectionRef || ref}
      aria-labelledby={titleId}
      tabIndex={-1}
    >
      <EditorialSection
        index={4}
        eyebrow="Intelligence"
        title="Building with AI"
        headingId={titleId}
        meta="LLMS · RAG · RL"
      />

      <p className="ai__lede">
        I build AI-powered products end-to-end — integrating LLMs and
        retrieval-augmented generation into real apps, plus hands-on work in
        reinforcement learning and image processing.
      </p>

      <ol className="ai__capabilities">
        {CAPABILITIES.map((cap, i) => (
          <li
            className="ai__capability"
            data-accent={cap.accent}
            key={cap.id}
          >
            <span className="ai__capability-num" aria-hidden="true">
              {num(i)}
            </span>
            <div className="ai__capability-body">
              <h3 className="ai__capability-title">{cap.label}</h3>
              <p className="ai__capability-desc">{cap.description}</p>
            </div>
          </li>
        ))}
      </ol>

      <div className="ai__cta">
        <button
          type="button"
          className="ai__ask"
          aria-label="Ask my AI assistant about Sébastien's AI experience"
          onClick={askMyAI}
        >
          <span className="ai__ask-spark" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" focusable="false">
              <path
                d="M12 2.5c.5 3.4 1.6 5 5 5.5-3.4.5-4.5 2.1-5 5.5-.5-3.4-1.6-5-5-5.5 3.4-.5 4.5-2.1 5-5.5Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className="ai__ask-label">Ask my AI anything</span>
          <span className="ai__ask-arrow" aria-hidden="true">→</span>
        </button>
        <p className="ai__cta-note">
          A scripted, front-end demo assistant — no live model, just a quick way
          to explore my work.
        </p>
      </div>
    </Section>
  );
});

AI.displayName = 'AI';

export default AI;
